// Thin My Desk panel wrappers around boards that already exist elsewhere in
// the app (Company's Operations/Management tabs, Reports, Money) and are
// already self-contained — each one fetches its own data and needs no new
// API. This file adds NOTHING beyond a DeskPanel wrapper and clinic
// resolution: same component, same behaviour, just addressable from the
// panel catalog and scoped by config.clinic instead of a page's own
// ClinicTabs selection.
//
// Every one of these takes a SPECIFIC clinic and shows only that clinic's
// board — there is no "all clinics" mode, unlike payHours. A saved config
// naming a clinic the viewer can no longer see falls back to the viewer's
// first visible clinic rather than rendering nothing (INV-9).
import type { JSX } from 'preact';
import { visibleClinics } from '../components/ClinicTabs';
import { LOCATION_NAMES, type LocationCode } from '../lib/locations';
import { DeskPanel } from '../components/DeskPanel';
import { ServiceCompliance } from '../components/ServiceCompliance';
import { ServicePoints } from '../components/ServicePoints';
import { BonusTracker } from '../components/BonusTracker';
import { ReportScheduleBoard } from '../components/ReportScheduleBoard';
import { ReportBoard } from '../components/ReportBoard';
import { DepositReconGrid } from '../components/DepositReconGrid';
import { PayHoursBoard } from '../components/PayHours';

export function resolveClinic(configClinic: string | undefined, visible: LocationCode[]): LocationCode | null {
  if (configClinic && configClinic !== 'all' && visible.includes(configClinic as LocationCode)) {
    return configClinic as LocationCode;
  }
  return visible[0] ?? null;
}

interface ClinicPanelProps { config?: { clinic?: string }; chromeless?: boolean }

function ClinicScopedPanel({
  title, testId, className, config, chromeless, Board,
}: {
  title: string;
  testId: string;
  className?: string;
  config?: { clinic?: string };
  chromeless?: boolean;
  Board: (props: { clinic: LocationCode }) => JSX.Element;
}): JSX.Element | null {
  const visible = visibleClinics();
  const clinic = resolveClinic(config?.clinic, visible);
  if (!clinic) return null;
  return (
    <DeskPanel
      title={title}
      subtitle={LOCATION_NAMES[clinic]}
      testId={testId}
      className={className}
      chromeless={chromeless}
    >
      <div class="desk-panel-scroll">
        <Board clinic={clinic} />
      </div>
    </DeskPanel>
  );
}

export function SpcListForClinicPanel({ config, chromeless }: ClinicPanelProps): JSX.Element | null {
  return (
    <ClinicScopedPanel
      title="SPC"
      testId="spc-list-for-clinic-panel"
      config={config}
      chromeless={chromeless}
      Board={({ clinic }) => <ServiceCompliance clinic={clinic} />}
    />
  );
}

export function ServicePointsForClinicPanel({ config, chromeless }: ClinicPanelProps): JSX.Element | null {
  return (
    <ClinicScopedPanel
      title="Service Points"
      testId="service-points-for-clinic-panel"
      config={config}
      chromeless={chromeless}
      Board={({ clinic }) => <ServicePoints clinic={clinic} />}
    />
  );
}

export function BonusTrackerForClinicPanel({ config, chromeless }: ClinicPanelProps): JSX.Element | null {
  return (
    <ClinicScopedPanel
      title="Bonus Tracker"
      testId="bonus-tracker-for-clinic-panel"
      config={config}
      chromeless={chromeless}
      Board={({ clinic }) => <BonusTracker clinic={clinic} />}
    />
  );
}

export function ReportScheduleForClinicPanel({ config, chromeless }: ClinicPanelProps): JSX.Element | null {
  return (
    <ClinicScopedPanel
      title="Report Schedule"
      testId="report-schedule-for-clinic-panel"
      config={config}
      chromeless={chromeless}
      Board={({ clinic }) => <ReportScheduleBoard clinic={clinic} />}
    />
  );
}

export function ReportBoardForClinicPanel({ config, chromeless }: ClinicPanelProps): JSX.Element | null {
  return (
    <ClinicScopedPanel
      title="Reports"
      testId="report-board-for-clinic-panel"
      config={config}
      chromeless={chromeless}
      Board={({ clinic }) => <ReportBoard clinic={clinic} />}
    />
  );
}

export function DepositReconForClinicPanel({ config, chromeless }: ClinicPanelProps): JSX.Element | null {
  return (
    <ClinicScopedPanel
      title="Deposit Recon"
      testId="deposit-recon-for-clinic-panel"
      config={config}
      chromeless={chromeless}
      Board={({ clinic }) => <DepositReconGrid locationCode={clinic} />}
    />
  );
}

/** Owner, 2026-09-10: "should be the Pay and Hours Table" — the People tab's
 * table for one clinic, on the desk. */
export function PayHoursTableForClinicPanel({ config, chromeless }: ClinicPanelProps): JSX.Element | null {
  return (
    <ClinicScopedPanel
      title="Pay & Hours"
      testId="pay-hours-table-for-clinic-panel"
      config={config}
      chromeless={chromeless}
      Board={({ clinic }) => <PayHoursBoard clinic={clinic} />}
    />
  );
}
